import { useRef, useEffect, useState } from "react";

type StarTier = "dim" | "mid" | "bright";

interface Star {
  x: number;
  y: number;
  radius: number;
  opacity: number;
  twinkleSpeed: number;
  phase: number;
  tier: StarTier;
}

const TIER_CONFIG: Record<StarTier, { opacity: number; radiusMin: number; radiusMax: number; twinkle: boolean }> = {
  dim:    { opacity: 0.2,  radiusMin: 0.3, radiusMax: 0.8,  twinkle: false },
  mid:    { opacity: 0.45, radiusMin: 0.5, radiusMax: 1.0,  twinkle: false },
  bright: { opacity: 0.75, radiusMin: 0.8, radiusMax: 1.5, twinkle: true },
};

// Distribution: 50% dim, 30% mid, 20% bright
function pickTier(): StarTier {
  const r = Math.random();
  if (r < 0.5) return "dim";
  if (r < 0.8) return "mid";
  return "bright";
}

function createStars(count: number, w: number, h: number): Star[] {
  const stars: Star[] = [];
  for (let i = 0; i < count; i++) {
    const tier = pickTier();
    const cfg = TIER_CONFIG[tier];
    stars.push({
      x: Math.random() * w,
      y: Math.random() * h,
      radius: Math.random() * (cfg.radiusMax - cfg.radiusMin) + cfg.radiusMin,
      opacity: cfg.opacity + (Math.random() * 0.05 - 0.025),
      twinkleSpeed: cfg.twinkle ? (Math.random() * 0.003 + 0.001) : 0,
      phase: Math.random() * Math.PI * 2,
      tier,
    });
  }
  return stars;
}

export function DesktopBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const rafRef = useRef<number>(0);
  const [wallpaperUrl, setWallpaperUrl] = useState(() => {
    const saved = localStorage.getItem("opentool-active-wallpaper");
    return saved ? `/wallpapers/${saved}.svg` : "/wallpapers/circuit-board.svg";
  });

  // Listen for wallpaper changes from WallpaperWorkbench
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.url) setWallpaperUrl(detail.url);
      else if (detail?.id) setWallpaperUrl(`/wallpapers/${detail.id}.svg`);
    };
    window.addEventListener("opentool-wallpaper-change", handler);
    return () => window.removeEventListener("opentool-wallpaper-change", handler);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      starsRef.current = createStars(280, canvas.width, canvas.height);
    };

    resize();
    window.addEventListener("resize", resize);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (prefersReduced) {
      // Static render
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const star of starsRef.current) {
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      return () => window.removeEventListener("resize", resize);
    }

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const star of starsRef.current) {
        let alpha = star.opacity;
        if (star.twinkleSpeed > 0) {
          // Bright stars twinkle
          const twinkle = Math.sin(time * star.twinkleSpeed + star.phase) * 0.35 + 0.65;
          alpha = star.opacity * twinkle;
        }

        // Slight color tint for bright stars
        if (star.tier === "bright") {
          const hue = (star.phase * 30) % 60; // slight blue-to-white variation
          ctx.fillStyle = `hsla(${210 + hue}, 60%, 85%, ${alpha})`;
        } else {
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fill();

        // Add a subtle glow halo around bright stars
        if (star.tier === "bright" && alpha > 0.5) {
          ctx.fillStyle = `rgba(180, 200, 255, ${alpha * 0.08})`;
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.radius * 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      {/* Nebula gradient patches behind the canvas */}
      <div className="wm-desktop__nebula" />
      <canvas ref={canvasRef} className="wm-desktop__canvas" />
      {/* Circuit board SVG wallpaper — shown only by .wm-desktop--circuit-board */}
      <div
        className="wm-desktop__circuit-wallpaper"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          backgroundImage: `url(${wallpaperUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 1,
        }}
      />
    </>
  );
}
